import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  TextField,
  Avatar,
  Chip,
  Collapse,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CloseIcon from "@mui/icons-material/Close";
import PenPaperIcon from "../../../../components/icons/PenPaperIcon";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { VenueFormData } from "../../../../types/venue";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import ContactSelector, {
  IContact,
} from "../../view-venue/components/ContactAutoComplete";

// ── types ──────────────────────────────────────────────────────────────────

interface NewContactForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  contact_type: "individual";
}

interface ContactsProps {
  formData: VenueFormData;
  handleInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
  editingContactId?: string | null;
  setEditingContactId?: (id: string | null) => void;
}

type Mode = "idle" | "search" | "create";

// ── Styled components (OUTSIDE to prevent remounting) ──────────────────────

const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: textcolor,
  marginBottom: "6px",
  opacity: 0.7,
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "12px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "11px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
  }),
);

// ── helpers ────────────────────────────────────────────────────────────────

const getInitials = (name: string, surname: string) =>
  `${name?.[0] ?? ""}${surname?.[0] ?? ""}`.toUpperCase() || "?";

const avatarColor = (id: string) => {
  const colors = [
    "#6B3A2A",
    "#2A4A6B",
    "#2A6B3A",
    "#6B2A5A",
    "#5A6B2A",
    "#2A5A6B",
  ];
  const idx = id.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return colors[idx % colors.length];
};

// ── ContactFormFields (OUTSIDE component to prevent focus loss on re-render) ──

const ContactFormFields = ({
  values,
  onChange,
  onPhoneChange,
  color100,
  color300,
  color700,
  color800,
  pillTelSx,
}: {
  values: NewContactForm;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onPhoneChange: (value: string, info: MuiTelInputInfo) => void;
  color100?: string;
  color300?: string;
  color700?: string;
  color800?: string;
  pillTelSx: object;
}) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12, md: 4 }}>
      <FieldLabel textcolor={color100}>First Name</FieldLabel>
      <StyledTextField
        name="first_name"
        placeholder="e.g. John"
        value={values.first_name}
        onChange={onChange}
        inputbg={color800}
        textcolor={color700}
        placeholdercolor={color300}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <FieldLabel textcolor={color100}>Last Name</FieldLabel>
      <StyledTextField
        name="last_name"
        placeholder="e.g. Doe"
        value={values.last_name}
        onChange={onChange}
        inputbg={color800}
        textcolor={color700}
        placeholdercolor={color300}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <FieldLabel textcolor={color100}>Role</FieldLabel>
      <StyledTextField
        name="role"
        placeholder="e.g. Manager"
        value={values.role}
        onChange={onChange}
        inputbg={color800}
        textcolor={color700}
        placeholdercolor={color300}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <FieldLabel textcolor={color100}>Email</FieldLabel>
      <StyledTextField
        name="email"
        placeholder="e.g. john@example.com"
        type="email"
        value={values.email}
        onChange={onChange}
        inputbg={color800}
        textcolor={color700}
        placeholdercolor={color300}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <FieldLabel textcolor={color100}>Phone</FieldLabel>
      <MuiTelInput
        fullWidth
        value={values.phone}
        defaultCountry="ZW"
        onChange={onPhoneChange}
        placeholder="e.g. +263 77 123 4567"
        sx={pillTelSx}
      />
    </Grid>
  </Grid>
);

// ── Component ──────────────────────────────────────────────────────────────

export default function Contacts({
  formData,
  setFormData,
  editingContactId,
  setEditingContactId,
}: ContactsProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color300 = reduxTheme.primary_colors?.[300];
  const colorPrimary = reduxTheme.primary_colors?.[500];

  const contacts = useMemo(() => formData.contacts ?? [], [formData.contacts]);
  const [mode, setMode] = useState<Mode>("idle");
  const [savingContact, setSavingContact] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const emptyForm: NewContactForm = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "",
    contact_type: "individual",
  };

  const [newContactForm, setNewContactForm] =
    useState<NewContactForm>(emptyForm);
  const [editForm, setEditForm] = useState<NewContactForm>(emptyForm);

  // ── Shared sx (stable object — defined inside component but not recreated
  //    on each render because it only depends on theme colors which are stable) ──

  const pillTelSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: color800,
      borderRadius: "12px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "11px 14px",
      fontSize: "13px",
      color: color700,
      "&::placeholder": { color: color300, opacity: 0.9 },
    },
    "& .MuiTelInput-Flag": { ml: "12px" },
  };

  const searchSelectorSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: color800,
      borderRadius: "12px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 12px !important",
      fontSize: "13px",
      color: color700,
      "&::placeholder": { color: color300, opacity: 0.9 },
    },
    "& .MuiAutocomplete-endAdornment": {
      "& .MuiSvgIcon-root": { color: color300 },
    },
    "& .MuiInputLabel-root": { display: "none" },
    "& .MuiInputBase-root": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
  };

  // ── Open edit form when triggered from preview ─────────────────────────
  useEffect(() => {
    if (!editingContactId) return;

    const contact = contacts.find((c) => c.id === editingContactId);
    if (!contact) return;

    setEditForm({
      first_name: contact.name,
      last_name: contact.surname,
      email: contact.email,
      phone: contact.phone,
      role: contact.role,
      contact_type: "individual",
    });

    setTimeout(() => {
      const el = document.getElementById(
        `contact-accordion-${editingContactId}`,
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [editingContactId, contacts]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleContactSelectorChange = (contact: IContact | null) => {
    if (!contact) return;
    const alreadyAdded = contacts.some((c) => c.id === String(contact.id));
    if (alreadyAdded) {
      toast.info("Contact already added.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      contacts: [
        ...(prev.contacts ?? []),
        {
          id: String(contact.id),
          name: contact.first_name ?? "",
          surname: contact.last_name ?? "",
          role: "",
          email: contact.email ?? "",
          phone: contact.phone ?? "",
        },
      ],
      contacts_ids: [...(prev.contacts_ids ?? []), Number(contact.id)],
    }));
    setMode("idle");
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setNewContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setNewContactForm((prev) => ({ ...prev, phone: value }));
  };

  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditPhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setEditForm((prev) => ({ ...prev, phone: value }));
  };

  const openEdit = (contact: (typeof contacts)[0]) => {
    setEditForm({
      first_name: contact.name,
      last_name: contact.surname,
      email: contact.email,
      phone: contact.phone,
      role: contact.role,
      contact_type: "individual",
    });
    setEditingContactId?.(contact.id);
  };

  const cancelEdit = () => {
    setEditingContactId?.(null);
    setEditForm(emptyForm);
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const res = await apiService.post<IContact>(
        "/api/contacts/contact/",
        newContactForm,
      );
      const saved = res.data;
      setFormData((prev) => ({
        ...prev,
        contacts: [
          ...(prev.contacts ?? []),
          {
            id: String(saved.id),
            name: saved.first_name ?? "",
            surname: saved.last_name ?? "",
            role: saved.role ?? newContactForm.role,
            email: saved.email ?? "",
            phone: saved.phone ?? "",
          },
        ],
        contacts_ids: [...(prev.contacts_ids ?? []), Number(saved.id)],
      }));
      toast.success("Contact added successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
      setNewContactForm(emptyForm);
      setMode("idle");
    } catch (error) {
      const errorData = (
        error as { response?: { data?: Record<string, unknown> } }
      )?.response?.data;
      const message = errorData
        ? Object.values(errorData).flat().join(" ")
        : "Failed to add contact. Please try again.";
      toast.error(message, { position: "top-right", autoClose: 5000 });
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingContactId) return;
    setSavingEdit(true);
    try {
      const res = await apiService.patch<IContact>(
        `/api/contacts/contact/${editingContactId}/`,
        editForm,
      );
      const saved = res.data;
      setFormData((prev) => ({
        ...prev,
        contacts: (prev.contacts ?? []).map((c) =>
          c.id === editingContactId
            ? {
                ...c,
                name: saved.first_name ?? "",
                surname: saved.last_name ?? "",
                email: saved.email ?? "",
                phone: saved.phone ?? "",
                role: saved.role ?? editForm.role,
              }
            : c,
        ),
      }));
      toast.success("Contact updated successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
      cancelEdit();
    } catch (error) {
      const errorData = (
        error as { response?: { data?: Record<string, unknown> } }
      )?.response?.data;
      const message = errorData
        ? Object.values(errorData).flat().join(" ")
        : "Failed to update contact. Please try again.";
      toast.error(message, { position: "top-right", autoClose: 5000 });
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteContact = (contactId: string) => {
    setFormData((prev) => ({
      ...prev,
      contacts: (prev.contacts ?? []).filter((c) => c.id !== contactId),
      contacts_ids: (prev.contacts_ids ?? []).filter(
        (id) => id !== Number(contactId),
      ),
    }));
    if (editingContactId === contactId) cancelEdit();
  };

  // ── render ─────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Entry panel ── */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: alpha(color300 ?? "#ccc", 0.3),
          borderRadius: "16px",
          p: 2.5,
          mb: 3,
          backgroundColor: alpha(color800 ?? "#f5f5f5", 0.4),
        }}
      >
        {/* Mode toggle */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: mode !== "idle" ? 2.5 : 0,
            p: 1.5,
            borderRadius: "14px",
            backgroundColor: alpha(color300 ?? "#ccc", 0.08),
            border: "1px solid",
            borderColor: alpha(color300 ?? "#ccc", 0.15),
            width: "fit-content",
          }}
        >
          {[
            {
              m: "search" as Mode,
              icon: <SearchIcon sx={{ fontSize: 20 }} />,
              label: "Search Existing",
            },
            {
              m: "create" as Mode,
              icon: <PersonAddAlt1Icon sx={{ fontSize: 20 }} />,
              label: "Create New",
            },
          ].map(({ m, icon, label }) => (
            <Box
              key={m}
              onClick={() => setMode((prev) => (prev === m ? "idle" : m))}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.6,
                cursor: "pointer",
                opacity: mode !== "idle" && mode !== m ? 0.45 : 1,
                transition: "opacity 0.2s ease",
                minWidth: 80,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    mode === m ? colorPrimary : alpha(color300 ?? "#ccc", 0.15),
                  color: mode === m ? "#fff" : color100,
                  border: "1.5px solid",
                  borderColor:
                    mode === m ? colorPrimary : alpha(color300 ?? "#ccc", 0.25),
                  transition: "all 0.2s ease",
                  boxShadow:
                    mode === m
                      ? `0 4px 12px ${alpha(colorPrimary ?? "#ccc", 0.35)}`
                      : "none",
                }}
              >
                {icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: mode === m ? color100 : color300,
                  letterSpacing: "0.02em",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Search panel */}
        <Collapse in={mode === "search"}>
          <FieldLabel textcolor={color100}>Search by name or email</FieldLabel>
          <Box sx={searchSelectorSx}>
            <ContactSelector
              value={null}
              onChange={handleContactSelectorChange}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ color: color300, mt: 1, display: "block" }}
          >
            Selecting a contact will add them instantly.
          </Typography>
        </Collapse>

        {/* Create panel */}
        <Collapse in={mode === "create"}>
          <ContactFormFields
            values={newContactForm}
            onChange={handleFormChange}
            onPhoneChange={handlePhoneChange}
            color100={color100}
            color300={color300}
            color700={color700}
            color800={color800}
            pillTelSx={pillTelSx}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 2.5,
            }}
          >
            <Button
              onClick={() => setMode("idle")}
              startIcon={<CloseIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "13px",
                color: color300,
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveContact}
              disabled={savingContact || !newContactForm.first_name}
              disableElevation
              variant="contained"
              startIcon={
                savingContact ? undefined : (
                  <AddIcon sx={{ fontSize: "15px !important" }} />
                )
              }
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: colorPrimary,
                color: "#fff",
                px: 2.5,
                "&:hover": {
                  backgroundColor: alpha(colorPrimary ?? "#ccc", 0.85),
                },
                "&.Mui-disabled": {
                  backgroundColor: color800,
                  color: color300,
                },
              }}
            >
              {savingContact ? (
                <CircularProgress size={16} sx={{ color: color300 }} />
              ) : (
                "Save Contact"
              )}
            </Button>
          </Box>
        </Collapse>
      </Box>

      {/* ── Contact cards ── */}
      {contacts.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: color300,
              }}
            >
              Added Contacts
            </Typography>
            <Chip
              label={contacts.length}
              size="small"
              sx={{
                height: 18,
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: alpha(colorPrimary ?? "#ccc", 0.15),
                color: colorPrimary,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {contacts.map((contact, index) => (
              <Box key={contact.id} id={`contact-accordion-${contact.id}`}>
                {/* ── Edit form (inline, expands above card) ── */}
                <Collapse in={editingContactId === contact.id}>
                  <Box
                    sx={{
                      border: "1.5px solid",
                      borderColor: alpha(colorPrimary ?? "#ccc", 0.35),
                      borderRadius: "14px",
                      p: 2.5,
                      mb: 1,
                      backgroundColor: alpha(color800 ?? "#f5f5f5", 0.6),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: color100,
                        }}
                      >
                        Editing:{" "}
                        {[contact.name, contact.surname]
                          .filter(Boolean)
                          .join(" ") || `Contact ${index + 1}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={cancelEdit}
                        sx={{ color: color300 }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>

                    <ContactFormFields
                      values={editForm}
                      onChange={handleEditFormChange}
                      onPhoneChange={handleEditPhoneChange}
                      color100={color100}
                      color300={color300}
                      color700={color700}
                      color800={color800}
                      pillTelSx={pillTelSx}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 2.5,
                      }}
                    >
                      <Button
                        onClick={cancelEdit}
                        sx={{
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: "13px",
                          color: color300,
                          px: 2,
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveEdit}
                        disabled={savingEdit || !editForm.first_name}
                        disableElevation
                        variant="contained"
                        startIcon={
                          savingEdit ? undefined : (
                            <SaveOutlinedIcon
                              sx={{ fontSize: "15px !important" }}
                            />
                          )
                        }
                        sx={{
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: "13px",
                          fontWeight: 600,
                          backgroundColor: colorPrimary,
                          color: "#fff",
                          px: 2.5,
                          "&:hover": {
                            backgroundColor: alpha(
                              colorPrimary ?? "#ccc",
                              0.85,
                            ),
                          },
                          "&.Mui-disabled": {
                            backgroundColor: color800,
                            color: color300,
                          },
                        }}
                      >
                        {savingEdit ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: color300 }}
                          />
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Collapse>

                {/* ── Contact card ── */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor:
                      editingContactId === contact.id
                        ? alpha(colorPrimary ?? "#ccc", 0.4)
                        : alpha(color300 ?? "#ccc", 0.2),
                    backgroundColor:
                      editingContactId === contact.id
                        ? alpha(colorPrimary ?? "#ccc", 0.04)
                        : alpha(color800 ?? "#f5f5f5", 0.5),
                    transition: "all 0.2s ease",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: avatarColor(contact.id),
                      fontSize: "15px",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(contact.name, contact.surname)}
                  </Avatar>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: color100,
                        lineHeight: 1.3,
                      }}
                    >
                      {[contact.name, contact.surname]
                        .filter(Boolean)
                        .join(" ") || `Contact ${index + 1}`}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        mt: 0.5,
                      }}
                    >
                      {contact.role && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          }}
                        >
                          <WorkOutlineIcon
                            sx={{ fontSize: 12, color: color300 }}
                          />
                          <Typography
                            sx={{ fontSize: "12px", color: color300 }}
                          >
                            {contact.role}
                          </Typography>
                        </Box>
                      )}
                      {contact.email && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          }}
                        >
                          <EmailOutlinedIcon
                            sx={{ fontSize: 12, color: color300 }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: color300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 160,
                            }}
                          >
                            {contact.email}
                          </Typography>
                        </Box>
                      )}
                      {contact.phone && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                          }}
                        >
                          <PhoneOutlinedIcon
                            sx={{ fontSize: 12, color: color300 }}
                          />
                          <Typography
                            sx={{ fontSize: "12px", color: color300 }}
                          >
                            {contact.phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(contact)}
                      sx={{
                        color:
                          editingContactId === contact.id
                            ? colorPrimary
                            : color300,
                        opacity: 0.8,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: alpha(colorPrimary ?? "#ccc", 0.08),
                        },
                      }}
                    >
                      <PenPaperIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteContact(contact.id)}
                      sx={{
                        color: "error.light",
                        opacity: 0.7,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: alpha("#f44336", 0.08),
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
